import axiosInstance from "../axiosInstance";

/**
 * Historical Shopify sub-channel review (`/api/order-channel-review`).
 *
 * HISTORICAL ORDERS ONLY. `investigate` is a pure read — nothing is written
 * until `decide` is called with an explicit action, which is what lets the page
 * show candidates without any risk of changing data.
 */
export const orderChannelReviewService = {
  /**
   * Candidate (or already-reviewed) orders in a date range.
   * @param {object} args
   * @param {string} args.startDate YYYY-MM-DD, inclusive
   * @param {string} args.endDate   YYYY-MM-DD, inclusive
   * @param {string} [args.clientId]
   * @param {"pending"|"reviewed"|"reconcile"} [args.mode] `reconcile` lists
   *   approved decisions whose source_channel no longer matches (e.g. reverted
   *   by a later provider sync), each with a `reconcile_state`.
   */
  investigate: async ({
    clientId,
    startDate,
    endDate,
    mode = "pending",
    skip = 0,
    limit = 25,
  } = {}) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    params.set("mode", mode);
    params.set("$skip", String(skip));
    params.set("$limit", String(limit));

    const response = await axiosInstance.get(
      `/order-channel-review?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * Apply a reviewed decision. The ONLY call in this module that writes.
   *
   * `scope: "selected"` acts on `orderIds`. `scope: "query"` acts on the whole
   * currently-queried candidate set (Approve All) — the server re-runs the
   * investigation rather than trusting an id list from the browser, so the set
   * acted on is exactly the set the confirmation counted.
   *
   * `action: "reconcile"` restores previously approved classifications from
   * each order's own audit record (never a browser-supplied target). With
   * `dryRun: true` it writes nothing and returns counts + a preview sample.
   * `override: true` restores ONE order whose tag evidence has changed.
   *
   * Chunked repair: pass `chunkSize` (+ `afterId`/`runId` from the previous
   * response) to process one chunk per call; the response carries
   * `next_after_id`, `done` and `run_id` for the next call.
   */
  decide: async ({
    action,
    scope = "selected",
    orderIds = [],
    clientId,
    startDate,
    endDate,
    resolvedSourceChannel,
    note,
    dryRun,
    override,
    chunkSize,
    afterId,
    runId,
  } = {}) => {
    const body = { action, scope };
    if (scope === "selected") body.order_ids = orderIds;
    if (clientId) body.client_id = clientId;
    if (startDate) body.start_date = startDate;
    if (endDate) body.end_date = endDate;
    if (resolvedSourceChannel) body.resolved_source_channel = resolvedSourceChannel;
    if (note) body.note = note;
    if (dryRun) body.dry_run = true;
    if (override) body.override = true;
    if (chunkSize) body.chunk_size = chunkSize;
    if (afterId) body.after_id = afterId;
    if (runId) body.run_id = runId;

    const response = await axiosInstance.post("/order-channel-review", body);
    return response.data;
  },
};
